import { formatDateLabel, formatDueLabel } from '../dateLabels';
import { type BookingStatus } from './types';

/**
 * "Your next step" — the one action the dashboard puts above everything else.
 *
 * The ranking is the product decision this file exists to encode. It is
 * strictly ordered, and the first rank with usable data wins:
 *
 *   1. urgent_message      a clinician has flagged a thread as urgent
 *   2. overdue_action      a dated plan item whose date has passed
 *   3. testing_due         a scheduled test in the next fortnight
 *   4. unread_message      an unread message from the care team
 *   5. results_ready       a batch of lab results they have not seen
 *   6. report_available    a released report they have not opened
 *   7. incomplete_history  the history at /history, which gates everything
 *                          downstream — including booking the interview
 *   8. daily_bite          today's check-in
 *
 * Three deliberate design choices:
 *
 * `urgent_message` rides on a conversation, not on a clinical escalation.
 * Escalation records are staff-only and written in ops shorthand; the
 * member-facing event that should follow one is a clinician getting in touch.
 * Nothing writes the urgency flag yet, so this rank sits defined and unfilled —
 * that is its intended resting state, not a gap.
 *
 * `results_ready` sits BELOW `unread_message` on purpose. If a clinician has
 * already written to a member about their results, that message is the better
 * next step — it has a person and a judgement in it, where this rank has only a
 * count. It sits ABOVE `report_available` because raw results reach a member
 * before the quarterly report that interprets them, and the gap between the two
 * is where "so where do I actually look?" was being asked.
 *
 * The input is flat and primitive-only rather than a bundle of Firestore
 * records, so the ranking can be tested as a truth table with no fixtures. The
 * hook does the adapting.
 */

export type NextStepRank =
  | 'urgent_message'
  | 'overdue_action'
  | 'testing_due'
  | 'unread_message'
  | 'results_ready'
  | 'report_available'
  | 'incomplete_history'
  | 'daily_bite';

export const NEXT_STEP_ORDER: readonly NextStepRank[] = [
  'urgent_message',
  'overdue_action',
  'testing_due',
  'unread_message',
  'results_ready',
  'report_available',
  'incomplete_history',
  'daily_bite',
];

export type NextStepAccent = 'oxblood' | 'olive' | 'terracotta' | 'sea';

export interface NextStepAction {
  rank: NextStepRank;
  rankIndex: number;
  eyebrow: string;
  title: string;
  body: string;
  ctaLabel: string;
  ctaHref: string;
  accent: NextStepAccent;
}

export interface NextStepOverdueItem {
  title: string;
  dueAt: Date | null;
  daysOverdue: number;
}

export interface NextStepScheduledTest {
  name: string;
  scheduledFor: Date | null;
}

export interface NextStepInput {
  hasUrgentMessage: boolean;
  unreadCount: number;
  /** Sender of the most recent unread thread, for the body copy. */
  unreadFrom?: string | null;
  overdueItems: NextStepOverdueItem[];
  nextScheduledTest: NextStepScheduledTest | null;
  /** A released report the member has not opened yet, if any. */
  unopenedReport: { id: string; quarterLabel: string } | null;
  /**
   * A batch of lab results the member has not looked at yet, if any. The hook
   * owns both the batching and the seen/unseen decision; by the time it reaches
   * here the only question left is where it ranks.
   */
  unseenResults: {
    count: number;
    outOfRangeCount: number;
    isFirstPanel: boolean;
  } | null;
  readiness: { completionRatio: number; ready: boolean } | null;
  bookingStatus: BookingStatus;
  dailyBite: { optedInCount: number; unlogged: number; streak: number } | null;
  /**
   * Ranks whose data failed to load. Those ranks are skipped entirely rather
   * than evaluated against a fallback value — a denied read must not be able to
   * silently promote or demote what the member is told to do next.
   */
  unavailableRanks?: NextStepRank[];
}

/** A scheduled test this far out or nearer is worth leading with. */
const TESTING_DUE_HORIZON_DAYS = 14;

function daysUntil(date: Date | null, now: Date): number | null {
  if (!date) return null;
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
}

function buildUrgentMessage(): NextStepAction {
  return {
    rank: 'urgent_message',
    rankIndex: 0,
    eyebrow: 'Needs your attention',
    title: 'Your care team needs to speak with you.',
    body: 'A message is waiting that your clinician marked as urgent.',
    ctaLabel: 'Read the message',
    ctaHref: '/messages',
    accent: 'oxblood',
  };
}

function buildOverdue(items: NextStepOverdueItem[]): NextStepAction | null {
  const worst = [...items].sort((left, right) => right.daysOverdue - left.daysOverdue)[0];
  if (!worst) return null;
  return {
    rank: 'overdue_action',
    rankIndex: 1,
    eyebrow: 'Overdue',
    title: worst.title,
    body:
      items.length > 1
        ? `${formatDueLabel(worst.dueAt)} — and ${items.length - 1} other item${items.length === 2 ? '' : 's'} on your plan is past its date.`
        : `${formatDueLabel(worst.dueAt)}. Your care team scheduled this as part of your monitoring plan.`,
    ctaLabel: 'View your plan',
    ctaHref: '/plan',
    accent: 'oxblood',
  };
}

function buildTestingDue(test: NextStepScheduledTest, days: number): NextStepAction {
  return {
    rank: 'testing_due',
    rankIndex: 2,
    eyebrow: 'Coming up',
    title: test.name,
    body:
      days <= 0
        ? 'Scheduled for today.'
        : `Scheduled for ${formatDateLabel(test.scheduledFor)} — ${days === 1 ? 'tomorrow' : `in ${days} days`}.`,
    ctaLabel: 'See your testing',
    ctaHref: '/testing',
    accent: 'terracotta',
  };
}

function buildUnreadMessage(count: number, from?: string | null): NextStepAction {
  return {
    rank: 'unread_message',
    rankIndex: 3,
    eyebrow: 'Message',
    title:
      count === 1
        ? `${from || 'Your care team'} sent you a message.`
        : `You have ${count} unread messages.`,
    body: 'Replies go straight to the clinicians looking after you.',
    ctaLabel: 'Open messages',
    ctaHref: '/messages',
    accent: 'sea',
  };
}

function buildResultsReady(results: {
  count: number;
  outOfRangeCount: number;
  isFirstPanel: boolean;
}): NextStepAction {
  const { count, outOfRangeCount, isFirstPanel } = results;

  // The body states counts and stops. Whether an out-of-range marker matters is
  // a clinical judgement, and this hero is not where Melio makes one — the
  // report and the care team are. "3 outside range" is descriptive; "3 results
  // need your attention" would not be.
  const rangeSentence =
    outOfRangeCount > 0
      ? `${outOfRangeCount} of them fall outside your reference range.`
      : 'All of them fall within your reference ranges.';

  return {
    rank: 'results_ready',
    rankIndex: 4,
    eyebrow: isFirstPanel ? 'Your first results' : 'Results are back',
    title:
      count === 1
        ? 'Your result is back.'
        : `Your blood results are back — ${count} markers.`,
    body: `${rangeSentence} Your care team reviews every result before it reaches you.`,
    ctaLabel: 'See your results',
    ctaHref: '/results',
    accent: outOfRangeCount > 0 ? 'terracotta' : 'olive',
  };
}

function buildReportAvailable(report: { id: string; quarterLabel: string }): NextStepAction {
  return {
    rank: 'report_available',
    rankIndex: 5,
    eyebrow: 'Ready to read',
    title: `Your ${report.quarterLabel} report is ready.`,
    body: 'Reviewed and signed off by a clinician before it was released to you.',
    ctaLabel: 'Read your report',
    ctaHref: `/reports/${report.id}`,
    accent: 'olive',
  };
}

function buildIncompleteHistory(
  readiness: { completionRatio: number },
  bookingStatus: BookingStatus,
): NextStepAction {
  const percent = Math.round(readiness.completionRatio * 100);

  // ALWAYS the history itself, whether or not the interview is booked.
  //
  // This used to send an unbooked member to /book/clinical-history and tell
  // them there were "no forms to fill in beforehand". That is the wrong way
  // round: filling in the history at /history is the first step, and the
  // interview follows it. Booking first left members with an appointment and
  // no idea the record was waiting for them — and the two surfaces that did
  // point at /history then contradicted this one on the same screen.
  if (percent === 0) {
    return {
      rank: 'incomplete_history',
      rankIndex: 6,
      eyebrow: 'Your first step',
      title: 'Start your health history.',
      body:
        bookingStatus === 'none'
          ? 'Your health and family history is the first thing we ask for — it is what your care team reads before anything else. You can book your interview once it is in.'
          : 'Your interview is booked. Filling in your health and family history beforehand is what the conversation works from.',
      ctaLabel: 'Start your history',
      ctaHref: '/history',
      accent: 'terracotta',
    };
  }

  return {
    rank: 'incomplete_history',
    rankIndex: 6,
    eyebrow: 'In progress',
    title: 'Your health history is still building.',
    body:
      bookingStatus === 'none'
        ? `${percent}% complete — finish it and you can book your clinical history interview.`
        : `${percent}% complete — add a few more details so your next review is as useful as possible.`,
    ctaLabel: 'Continue history',
    ctaHref: '/history',
    accent: 'olive',
  };
}

function buildDailyBite(dailyBite: { unlogged: number; streak: number }): NextStepAction {
  return {
    rank: 'daily_bite',
    rankIndex: 7,
    eyebrow: 'Today',
    title:
      dailyBite.streak > 0
        ? `Keep your ${dailyBite.streak}-day streak going.`
        : "A few minutes with today's check-in?",
    body: `${dailyBite.unlogged} tracker${dailyBite.unlogged === 1 ? '' : 's'} left — most take under five seconds.`,
    ctaLabel: "Finish today's check-in",
    ctaHref: '/today',
    accent: 'olive',
  };
}

/**
 * Returns the single highest-ranked action, or `null` when nothing is
 * outstanding. `null` is a designed state: the hero renders an affirmative
 * "you're all set" panel rather than going blank.
 */
export function computeNextStep(input: NextStepInput, now: Date = new Date()): NextStepAction | null {
  const unavailable = new Set(input.unavailableRanks || []);
  const available = (rank: NextStepRank) => !unavailable.has(rank);

  if (available('urgent_message') && input.hasUrgentMessage) {
    return buildUrgentMessage();
  }

  if (available('overdue_action') && input.overdueItems.length > 0) {
    const overdue = buildOverdue(input.overdueItems);
    if (overdue) return overdue;
  }

  if (available('testing_due') && input.nextScheduledTest) {
    const days = daysUntil(input.nextScheduledTest.scheduledFor, now);
    if (days !== null && days >= 0 && days <= TESTING_DUE_HORIZON_DAYS) {
      return buildTestingDue(input.nextScheduledTest, days);
    }
  }

  if (available('unread_message') && input.unreadCount > 0) {
    return buildUnreadMessage(input.unreadCount, input.unreadFrom);
  }

  if (available('results_ready') && input.unseenResults) {
    return buildResultsReady(input.unseenResults);
  }

  if (available('report_available') && input.unopenedReport) {
    return buildReportAvailable(input.unopenedReport);
  }

  if (available('incomplete_history') && input.readiness && !input.readiness.ready) {
    return buildIncompleteHistory(input.readiness, input.bookingStatus);
  }

  if (available('daily_bite') && input.dailyBite && input.dailyBite.optedInCount > 0 && input.dailyBite.unlogged > 0) {
    return buildDailyBite(input.dailyBite);
  }

  return null;
}
